import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, toQuery } from './client';
import type {
  Annotation,
  AnnotationKind,
  AppNotification,
  Coverage,
  Decision,
  Episode,
  EventFilters,
  EventPayload,
  ExportFormat,
  ExportRecord,
  Finding,
  FindingDetail,
  FindingFilters,
  Invitation,
  MetricsReport,
  OperationalEvent,
  OperationalEventType,
  OperationsResponse,
  Page,
  Preferences,
  Project,
  ReportResponse,
  ResearchCollection,
  ResearchMap,
  RetryGrant,
  ReviewDecision,
  Role,
  Source,
  SourceDetail,
  SourceSearchFilters,
  SourceVersionReview,
  Visibility,
} from './types';

export const keys = {
  me: ['me'] as const,
  report: ['report'] as const,
  findings: (filters: FindingFilters) => ['findings', filters] as const,
  finding: (id: string) => ['finding', id] as const,
  sourceSearch: (filters: SourceSearchFilters) => ['sources', 'search', filters] as const,
  source: (id: string) => ['source', id] as const,
  projects: ['projects'] as const,
  episodes: ['episodes'] as const,
  maps: ['maps'] as const,
  coverage: ['coverage'] as const,
  metrics: (project?: string) => ['metrics', project ?? null] as const,
  events: (filters: EventFilters) => ['events', filters] as const,
  notifications: ['notifications'] as const,
  collections: ['collections'] as const,
  exports: ['exports'] as const,
  operations: ['operations'] as const,
  invitations: ['invitations'] as const,
};

const enc = encodeURIComponent;

export const useReport = () =>
  useQuery({ queryKey: keys.report, queryFn: ({ signal }) => api.get<ReportResponse>('/api/report', signal) });

export const FINDING_PAGE_SIZE = 100;
const FINDING_BULK_SIZE = 500;
const MAX_FINDING_PAGES = 200;

const findingsUrl = (filters: FindingFilters, offset: string | undefined, limit: number) =>
  `/api/findings${toQuery({ ...filters, offset, limit: String(limit) })}`;

/**
 * Findings one bounded page at a time. The API's `nextCursor` is the next
 * offset, so it is sent back as `offset`.
 */
export const useFindingPages = (filters: FindingFilters, enabled = true) =>
  useInfiniteQuery({
    queryKey: [...keys.findings(filters), 'pages'],
    queryFn: ({ pageParam, signal }) => api.get<Page<Finding>>(findingsUrl(filters, pageParam, FINDING_PAGE_SIZE), signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });

/**
 * Every matching finding, following `nextCursor` to the end. For surfaces that
 * must be complete (steward queue, export choice, theme lists).
 */
export const useAllFindings = (filters: FindingFilters, enabled = true) =>
  useQuery({
    queryKey: [...keys.findings(filters), 'all'],
    queryFn: async ({ signal }): Promise<{ items: Finding[]; total: number }> => {
      const items: Finding[] = [];
      let offset: string | undefined;
      let total: number | undefined;
      for (let page = 0; page < MAX_FINDING_PAGES; page += 1) {
        const result = await api.get<Page<Finding>>(findingsUrl(filters, offset, FINDING_BULK_SIZE), signal);
        items.push(...result.items);
        total = result.total ?? total;
        const next = result.nextCursor ?? undefined;
        // A cursor that does not advance would loop forever; stop and report what arrived.
        if (next === undefined || next === offset || result.items.length === 0) break;
        offset = next;
      }
      return { items, total: total ?? items.length };
    },
    enabled,
  });

export const useFinding = (id: string) =>
  useQuery({
    queryKey: keys.finding(id),
    queryFn: ({ signal }) => api.get<FindingDetail>(`/api/findings/${enc(id)}`, signal),
  });

export const SOURCE_PAGE_SIZE = 50;

/**
 * Server-side source search over the whole visible archive, one bounded page
 * at a time. The first page is never treated as the complete set.
 */
export const useSourceSearch = (filters: SourceSearchFilters, enabled = true) =>
  useInfiniteQuery({
    queryKey: keys.sourceSearch(filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<Source>>(`/api/sources${toQuery({ ...filters, cursor: pageParam, limit: String(SOURCE_PAGE_SIZE) })}`, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });

export const useSource = (id: string, enabled = true) =>
  useQuery({
    queryKey: keys.source(id),
    queryFn: ({ signal }) => api.get<SourceDetail>(`/api/sources/${enc(id)}`, signal),
    enabled: enabled && id !== '',
  });

export const useProjects = () =>
  useQuery({ queryKey: keys.projects, queryFn: ({ signal }) => api.get<Page<Project>>('/api/projects', signal) });

/** GET /api/projects/:id returns the bare project; absent and inaccessible are both 404. */
export const useProject = (id: string) =>
  useQuery({
    queryKey: [...keys.projects, id],
    queryFn: ({ signal }) => api.get<Project>(`/api/projects/${enc(id)}`, signal),
    enabled: id !== '',
  });

export const useEpisodes = () =>
  useQuery({ queryKey: keys.episodes, queryFn: ({ signal }) => api.get<Page<Episode>>('/api/episodes', signal) });

export const useMaps = () =>
  useQuery({ queryKey: keys.maps, queryFn: ({ signal }) => api.get<Page<ResearchMap>>('/api/maps', signal) });

export const useCoverage = () =>
  useQuery({ queryKey: keys.coverage, queryFn: ({ signal }) => api.get<Coverage>('/api/coverage', signal) });

export const useMetrics = (project?: string) =>
  useQuery({
    queryKey: keys.metrics(project),
    queryFn: ({ signal }) => api.get<MetricsReport>(`/api/metrics${toQuery({ project })}`, signal),
  });

export const EVENT_PAGE_SIZE = 500;

/** Authorized workflow events; superseded events are already removed by the API. */
export const useEvents = (filters: EventFilters, enabled = true) =>
  useQuery({
    queryKey: keys.events(filters),
    queryFn: ({ signal }) =>
      api.get<Page<OperationalEvent>>(`/api/events${toQuery({ ...filters, limit: String(EVENT_PAGE_SIZE) })}`, signal),
    enabled,
  });

export const useNotifications = () =>
  useQuery({
    queryKey: keys.notifications,
    queryFn: ({ signal }) => api.get<Page<AppNotification>>('/api/notifications', signal),
  });

export const useCollections = () =>
  useQuery({
    queryKey: keys.collections,
    queryFn: ({ signal }) => api.get<Page<ResearchCollection>>('/api/collections', signal),
  });

export const useExports = () =>
  useQuery({ queryKey: keys.exports, queryFn: ({ signal }) => api.get<Page<ExportRecord>>('/api/exports', signal) });

export const useOperations = (enabled: boolean) =>
  useQuery({
    queryKey: keys.operations,
    queryFn: ({ signal }) => api.get<OperationsResponse>('/api/operations', signal),
    enabled,
  });

export const useInvitations = () =>
  useInfiniteQuery({
    queryKey: keys.invitations,
    queryFn: ({ pageParam, signal }) => api.get<Page<Invitation>>(`/api/invitations${toQuery({ cursor: pageParam, limit: '50' })}`, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

// ---- Mutations -------------------------------------------------------------

export interface AnnotationInput {
  kind: AnnotationKind;
  body: string;
  expectedVersion: number;
}

export function useAddAnnotation(findingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AnnotationInput) => api.post<{ annotation: Annotation }>(`/api/findings/${enc(findingId)}/annotations`, input),
    // A challenge, correction or proposal flags the finding for review and marks
    // dependent exports stale, so the lists that show those flags refetch too.
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.finding(findingId) }),
        client.invalidateQueries({ queryKey: keys.report }),
        client.invalidateQueries({ queryKey: ['findings'] }),
        client.invalidateQueries({ queryKey: keys.exports }),
        client.invalidateQueries({ queryKey: keys.notifications }),
      ]);
    },
  });
}

export interface ReviewInput {
  expectedVersion: number;
  decision: ReviewDecision;
  reason: string;
  changes?: Partial<Finding>;
}

export interface ReviewResult {
  finding: Finding;
  decision: Decision;
}

export function useReviewFinding(findingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewInput) => api.post<ReviewResult>(`/api/findings/${enc(findingId)}/review`, input),
    // Settled, not only success: a 409 means someone else changed the finding,
    // so every view must refetch the current version either way.
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.finding(findingId) }),
        client.invalidateQueries({ queryKey: keys.report }),
        client.invalidateQueries({ queryKey: ['findings'] }),
        client.invalidateQueries({ queryKey: keys.exports }),
        client.invalidateQueries({ queryKey: keys.notifications }),
      ]);
    },
  });
}

export type ProposalInput = Pick<
  Finding,
  | 'title'
  | 'summary'
  | 'chapter'
  | 'themes'
  | 'scope'
  | 'method'
  | 'interpretation'
  | 'uncertainty'
  | 'implications'
  | 'counterexamples'
  | 'evidence'
  | 'episodeIds'
  | 'sourceCutoff'
> & { projectId?: string };

export function useProposeFinding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ProposalInput) => api.post<{ finding: Finding }>('/api/findings', input),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['findings'] }),
        client.invalidateQueries({ queryKey: keys.report }),
      ]);
    },
  });
}

export interface CollectionInput {
  title: string;
  researchQuestion: string;
  findingIds: string[];
}

export function useCreateCollection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CollectionInput) => api.post<ResearchCollection>('/api/collections', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.collections }),
  });
}

export interface ExportInput {
  findingIds: string[];
  title: string;
  format: ExportFormat;
}

export function useCreateExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ExportInput) => api.post<ExportRecord>('/api/exports', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.exports }),
  });
}

export interface EventInput {
  type: OperationalEventType;
  projectId?: string;
  occurredAt: string;
  payload: EventPayload;
  visibility: Visibility;
}

export function useRecordEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: EventInput) => api.post<OperationalEvent>('/api/events', input),
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['metrics'] }),
        client.invalidateQueries({ queryKey: ['events'] }),
        client.invalidateQueries({ queryKey: keys.operations }),
      ]);
    },
  });
}

export function useUpdatePreferences() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Preferences) => api.patch<Preferences>('/api/me/preferences', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });
}

export interface InvitationInput {
  discordId: string;
  role: Role;
  /** false revokes access and ends that account's sessions. */
  active: boolean;
}

export function useSetInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: InvitationInput) => api.post<{ ok: boolean }>('/api/invitations', input),
    onSettled: () => client.invalidateQueries({ queryKey: keys.invitations }),
  });
}

export interface RetryJobInput {
  reason: string;
  /** One UUID per person's attempt, reused unchanged when resending after an unclear outcome. */
  idempotencyKey: string;
}

export interface RetryAnswer {
  grant: RetryGrant | undefined;
  /** When the server answered; job status is only trusted from operations data fetched after this. */
  answeredAt: number;
}

/** Steward request for one audited retry grant on a failed analysis job. */
export function useRetryAnalysisJob(jobId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: RetryJobInput): Promise<RetryAnswer> => {
      const grant = await api.post<RetryGrant | undefined>(`/api/operations/jobs/${enc(jobId)}/retry`, input);
      return { grant, answeredAt: Date.now() };
    },
    // Never retried automatically: only a person's explicit resend repeats the request.
    retry: false,
    onSettled: () => client.invalidateQueries({ queryKey: keys.operations }),
  });
}

export interface SourceReviewInput {
  versionId: string;
  expectedContentHash: string;
  memberContentApproved: boolean;
  reason: string;
}

/** Steward decision on whether members may read one exact stored version. */
export function useReviewSourceVersion(sourceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, ...body }: SourceReviewInput) =>
      api.post<{ review: SourceVersionReview }>(`/api/sources/${enc(sourceId)}/versions/${enc(versionId)}/review`, body),
    // Dependent findings are flagged for review server-side.
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.source(sourceId) }),
        client.invalidateQueries({ queryKey: ['finding'] }),
        client.invalidateQueries({ queryKey: ['findings'] }),
        client.invalidateQueries({ queryKey: keys.report }),
      ]);
    },
  });
}
